import { initializeApp } from 'https://www.gstatic.com/firebasejs/11.8.0/firebase-app.js';
import { getAuth, createUserWithEmailAndPassword, inMemoryPersistence, initializeAuth, sendEmailVerification, signInWithEmailAndPassword, updateEmail, updatePassword, updateProfile } from 'https://www.gstatic.com/firebasejs/11.8.0/firebase-auth.js';
import { getDatabase } from 'https://www.gstatic.com/firebasejs/11.8.0/firebase-database.js';
import { getFunctions, httpsCallable } from 'https://www.gstatic.com/firebasejs/11.8.0/firebase-functions.js';
import { getStorage } from 'https://www.gstatic.com/firebasejs/11.8.0/firebase-storage.js';

import archivo from './fonts/Archivo-VariableFont_wdth\,wght.woff2';
import orbitUrl from './orbit.svg';
import { capitalize, codeGrade, delegate, getDialog, getHeader, unifontFaces, getInputDialog, getPaymentDialog, getUTC, isLocked, loadKatex, logError, logMessage, safeGet as get, safeOnValue as onValue, safeRemove as remove, safeUpdate as update, titleCase, write } from './script';
import { OFFLINE, bootOffline } from './offline';
import css from './index.css';
      
const firebaseConfig = {
  apiKey: 'AIzaSyDwN16L_V26A7Hip-oeh1c0fI2ayfZH_nY',
  authDomain: 'zebula.firebaseapp.com',
  databaseURL: 'https://zebula.firebaseio.com',
  projectId: 'zebula',
  storageBucket: 'zebula.appspot.com',
  messagingSenderId: '422151292468',
  appId: '1:422151292468:web:cd889f19c5b7872eac6c61',
  measurementId: 'G-8JF65HQ96R'
};

// Two Firebase apps: the default one for teachers and parents, and one named 'student'. Firebase
// saves a login per APP NAME, and every tab of a site shares that storage, so with a single app
// a student signing in anywhere in the browser replaced the teacher's saved login — and the
// teacher's next reload came back as the student (user report, Sep 2026). A tab uses one app;
// everything else follows it, because these exports are live bindings read at call time, and
// the modules that reach for window.firebaseApp / window.firebaseFunctions read those then too.
const apps = { teacher: initializeApp(firebaseConfig), student: initializeApp(firebaseConfig, 'student') };
export let auth, database, functions, storage;
export function useApp(role) {
  const app = apps[role];
  if (window.firebaseApp === app) return;
  window.firebaseApp = app;
  auth = getAuth(app);
  database = getDatabase(app);
  functions = getFunctions(app);
  window.firebaseFunctions = functions;
  storage = getStorage(app);
  // Remembered for this tab only, so a reload comes back to the same login.
  try { if (role === 'student') sessionStorage.setItem('zebula-login', 'student'); else sessionStorage.removeItem('zebula-login'); } catch (error) {}
}
// Creating an account signs the creator in as it, so the dashboard makes student accounts on a sign-in of
// its own, held in memory only. Before this the teacher's own tab was signed in as each student it added,
// and the account functions, which now check that the caller is the student's teacher, would refuse it.
let creator;
export function creatorAuth() {
  creator ??= initializeAuth(initializeApp(firebaseConfig, 'creator'), { persistence: inMemoryPersistence });
  return creator;
}
// A tab opened on a student login link (name-color-emoji) or an enrollment link is a student
// tab from the start; so is one that signed a student in before a reload.
{
  const link = window.location.hash.replace(/#/, '');
  let remembered = null;
  try { remembered = sessionStorage.getItem('zebula-login'); } catch (error) {}
  useApp(link.split('-').length === 3 || link.startsWith('enroll') || remembered === 'student' ? 'student' : 'teacher');
}

window.__archivoFontUrl = archivo;

// Eagerly fetch the 4 core KaTeX faces at app startup so they're cache-warm
// well before any math renders (dash-teacher iframe mount, student slide
// open, etc.). Covers both student and teacher entry paths.
loadKatex();

let prevError;
// Exported so caught chunk-load failures can reach it too — every `await import()` in the app
// sits inside a try/catch, so the unhandledrejection wiring below never saw them and students
// stayed on a broken stale bundle until they refreshed by hand. script.js#logError calls this.
export let versionReload = () => {
  let reloaded = sessionStorage.getItem('zebula-reload');
  if (reloaded !== process.env.VERSION) {
    sessionStorage.setItem('zebula-reload', process.env.VERSION);
    let url = new URL(window.location.href);
    url.searchParams.set('v', Date.now());
    window.location.replace(url.href);
    return true;
  }
  return false;
};
// The delegated-events runner for this page, ahead of every other document listener (script.js act).
delegate(document);
window.addEventListener('error', async (event) =>{
  if (event.target?.tagName === 'SCRIPT' && event.target?.src?.includes('bundle.')) {
    if (versionReload()) return;
  }
  // Offline mode: skip the Cloud Function call (it would internally touch RTDB).
  if (OFFLINE) return;
  let logError = httpsCallable(firebaseFunctions, 'logError');
  let loc = `${event.filename || '?'}:${event.lineno || '?'}:${event.colno || '?'}`;
  // event.error is null for cross-origin script errors, non-Error throws, and a few quirky cases.
  // Fall back to event.message (sometimes set when event.error isn't), then to the source location,
  // so the dashboard log never shows a bare "Unknown error".
  let message = event.error?.message || event.message || `Uncaught error at ${loc}`;
  // Benign browser notice, not a fault: it fires when a ResizeObserver callback dirties
  // layout again in the same frame. Every observer we own already defers its work into a
  // rAF (the documented mitigation, see dash.js/slide.js) and it still surfaces now and
  // then — the spec treats it as a notification, nothing is dropped, and there is nothing
  // left to fix. Logging it just buries real errors.
  if (message.startsWith('ResizeObserver loop')) return;
  if (prevError === message) return;
  logError({ stack: event.error?.stack || `${loc}\n.`, message, name: window.z?.name || 'Someone', params: `&app=${window.z?.app || 'null'}&code=${window.z?.code || 'null'}&student=${window.z?.student || 'null'}&tooltip=${navigator.userAgent}&type=error&version=${window.z?.version || 'null'}`, teacher: window.z?.teacher || 'null' });
  prevError = message;
}, true);

window.addEventListener('unhandledrejection', async (event) => {
  let error = event.reason;
  let message = error?.message || String(error) || 'Unhandled promise rejection';
  if (error?.name === 'ChunkLoadError' || message.includes('Loading chunk') || message.includes('Failed to fetch dynamically imported module')) {
    if (versionReload()) return;
  }
  // Offline mode: skip the Cloud Function call (it would internally touch RTDB).
  if (OFFLINE) return;
  let logError = httpsCallable(firebaseFunctions, 'logError');
  if (prevError === message) return;
  logError({ stack: error?.stack || 'No stack trace', message: message, name: window.z?.name || 'Someone', params: `&app=${window.z?.app || 'null'}&code=${window.z?.code || 'null'}&student=${window.z?.student || 'null'}&tooltip=${navigator.userAgent}&type=rejection&version=${window.z?.version || 'null'}`, teacher: window.z?.teacher || 'null' });
  prevError = message;
});

let App, Dash, Game;
let style = document.createElement('style');
style.classList.add('style', 'index');
style.textContent = css;
document.head.appendChild(style);
window.name = 'main';

let code = window.location.pathname.split('/')[1];
let hash = window.location.hash.replace(/#/, '');
let archivoFont = new FontFace('Archivo', `url(${archivo})`);
// Unifont is two files of one family (script.js unifontFaces): the small one loads now, without blocking Auth;
// the full one only when text needs a character the small one lacks.
for (const face of unifontFaces) document.fonts.add(face);
unifontFaces[1].load().then(() => window.dispatchEvent(new Event('unifont-loaded'))).catch(() => {});
let katexReady = new Promise((resolve) => {
  if (window.renderMathInElement) return resolve();
  // KaTeX scripts have defer — poll until loaded (typically <100ms after fonts)
  let check = setInterval(() => {
    if (window.renderMathInElement) { clearInterval(check); resolve(); }
  }, 50);
});
Promise.all([
  archivoFont.load().then(() => document.fonts.add(archivoFont)),
  katexReady
]).then(() => { try { new Auth(code); } catch (error) { logError(error, 'initializing the app'); } });
let colors = [{ name: 'red', value: '#ff8080', mid: '#bf3939', dark: '#800d0d', darker: '#660a0a', darkest: '#260000', neighbor: '#8c4600', blend: '-50%' }, { name: 'orange', value: '#ffb366', mid: '#bf6e1d', dark: '#8c4600', darker: '#733900', darkest: '#261400', neighbor: '#800d0d', blend: '50%' }, { name: 'yellow', value: '#ffe666', mid: '#bfa939', dark: '#826b07', darker: '#665405', darkest: '#262100', neighbor: '#8c4600', blend: '100%' }, { name: 'green', value: '#73e699', mid: '#32a658', dark: '#0a6629', darker: '#084d1f', darkest: '#001a09', neighbor: '#826b07', blend: '0%' }, { name: 'teal', value: '#73e6d2', mid: '#32a692', dark: '#0a6657', darker: '#084d41', darkest: '#001a15', neighbor: '#0d5980', blend: '50%' }, { name: 'blue', value: '#80d5ff', mid: '#3993bf', dark: '#0d5980', darker: '#0a4766', darkest: '#001926', neighbor: '#471f99', blend: '50%' }, { name: 'indigo', value: '#aa80ff', mid: '#7447cc', dark: '#471f99', darker: '#361773', darkest: '#140533', neighbor: '#6e1980', blend: '-50%' }, { name: 'violet', value: '#ea80ff', mid: '#aa43bf', dark: '#6e1980', darker: '#581466', darkest: '#25032b', neighbor: '#471f99', blend: '50%' }, { name: 'pink', value: '#ff80bf', mid: '#bf4381', dark: '#80194d', darker: '#66143d', darkest: '#260315', neighbor: '#6e1980', blend: '100%' }, { name: 'white', value: '#fae1fa'}];
let emojis = [{ name: 'dog' }, { name: 'cat' }, { name: 'bird' }, { name: 'dolphin' }, { name: 'unicorn' }, { name: 'sunflower' }, { name: 'planet' }, { name: 'rose' }, { name: 'snowflake' }, { name: 'rainbow' }, { name: 'sparkles' }, { name: 'fire' }, { name: 'heart' }, { name: 'diamond' }, { name: 'crown' }, { name: 'burger' }, { name: 'pizza' }, { name: 'apple' }, { name: 'cupcake' }, { name: 'doughnut' }, { name: 'football' }, { name: 'basketball' }, { name: 'baseball' }, { name: 'music' }, { name: 'paint' }, { name: 'new' }];
let plans = { 'P-71357843ST238284WM2ROOSY': { name: 'Grade 5 Math', section: '5A', time: '9:00–9:30 AM PT', hours: 9, minutes: 0 }, 'P-1A631007S7514741SM3ITRWY': { name: 'Grade 5 Math', section: '5B', time: '1:00–1:30 PM PT', hours: 13, minutes: 0 }, 'https://outschool.com/classes/grade-6-math-BJwocdst?sectionUid=22671edd-0f6d-4fec-97e4-791d00cdc4d0&showDetails=true&usid=V9YXmhZQ&signup=true': { name: 'Grade 6 Math', section: '6A', time: '9:45–10:15 AM PT', hours: 9, minutes: 45 }, 'https://outschool.com/classes/grade-6-math-BJwocdst?sectionUid=666b2ca0-e7a2-421f-b97d-2391f507bc44&showDetails=true&usid=V9YXmhZQ&signup=true': { name: 'Grade 6 Math', section: '6B', time: '1:45–2:15 PM PT', hours: 13, minutes: 45 }, 'https://outschool.com/classes/grade-7-math-8gJbQgRi?sectionUid=a0e0c5f2-e8f7-4a57-bae6-69efeba3f487&showDetails=true&usid=V9YXmhZQ&signup=true': { name: 'Grade 7 Math', section: '7A', time: '10:30–11:00 AM PT', hours: 10, minutes: 30 }, 'https://outschool.com/classes/grade-7-math-8gJbQgRi?sectionUid=d427b5d3-e275-47b9-9784-bd469ce63396&showDetails=true&usid=V9YXmhZQ&signup=true': { name: 'Grade 7 Math', section: '7B', time: '2:30–3:00 PM PT', hours: 14, minutes: 30 }, 'https://outschool.com/classes/grade-8-math-9jB7AXEy?sectionUid=75856217-a36e-4590-81f7-1cf02d36f5bd&showDetails=true&usid=V9YXmhZQ&signup=true': { name: 'Grade 8 Math', section: '8A', time: '11:15–11:45 AM PT', hours: 11, minutes: 15 }, 'https://outschool.com/classes/grade-8-math-9jB7AXEy?sectionUid=6f046481-fd82-4dfa-9dc1-0e97fe80df88&showDetails=true&usid=V9YXmhZQ&signup=true': { name: 'Grade 8 Math', section: '8B', time: '3:15–3:45 PM PT', hours: 15, minutes: 15 } };
let topics = { 4: ['Place Value and Rounding','Multi-Digit Addition and Subtraction','Single Digit Multiplication','Double Digit Multiplication','Single Digit Long Division','Multiples and Factors','Compare Fractions','Add and Subtract Like Fractions','Multiply Fractions and Whole Numbers','Convert Fractions and Decimals','Compare Decimals','Area and Perimeter of Rectangles','Parallel and Perpendicular Lines','Measure and Classify Angles','Lines of Symmetry','Convert Units of Measurement'], 5: ['Place Value and Rounding','Add and Subtract Decimals','Add and Subtract Unlike Fractions','Add and Subtract Mixed Numbers','Multi-Digit Multiplication','Long Division','Multiply Fractions and Mixed Numbers','Divide Fractions and Whole Numbers','Multiply Decimals','Divide Decimals','Powers of Ten','Volume of Rectangular Prisms','Graph Points on a Coordinate Plane','Convert Units of Measurement','Line Plots','Classify Triangles and Quadrilaterals'], 6: ['Ratios','Unit Rates','Percents','Divide Fractions and Mixed Numbers','Long Division','Decimal Operations','Multiples and Factors','Rational Numbers','Exponents','Order of Operations','Algebraic Expressions','Equations','Area of Triangles and Quadrilaterals','Surface Area of Rectangular Prisms','Mean, Median, Mode, and Range','Histograms'], 7: ['Proportional Relationships','Unit Rates','Percents','Add and Subtract Rational Numbers','Multiply and Divide Rational Numbers','Convert Fractions to Decimals','Simplify Expressions','Factor and Expand Expressions','Equations','Inequalities','Area of Polygons','Volume and Surface Area of Prisms','Angle Relationships','Area and Circumference of Circles','Probability','Random Sampling'], 8: ['Exponents','Square and Cube Roots','Rational and Irrational Numbers','Scientific Notation','Equations','Slope of a Line','Slope-Intercept Form','Functions','Linear Models','Scatter Plots','Systems of Equations','Angle Relationships','Pythagorean Theorem','Volume of Cones, Cylinders, and Spheres','Reflections, Rotations, and Translations','Dilations'], 9: ['Equivalent Expressions', 'Linear Equations', 'Linear Functions', 'Graph Linear Functions', 'Write Linear Equations', 'Linear Inequalities', 'Systems of Equations and Inequalities', 'Exponents and Radicals', 'Add and Subtract Polynomials', 'Multiply Polynomials', 'Factor Polynomials', 'Quadratic Functions', 'Quadratic Equations', 'Exponential Functions', 'Sequences'] };
// `apps` starts empty: on the /bonus page nothing loads a lesson index before the bonus problem is stored in
// z.apps.keys, and the /apps/review download that used to create it was removed in 3.8.18 (the bonus page then
// crashed on z.apps.keys, Oct 2026).
export let z = { apps: {}, background: document.getElementById('background'), code: code, doc: document, loading: document.getElementById('loading'), topics: topics, version: process.env.VERSION, win: window };
if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') window.z = z;
fetch(orbitUrl).then(r => r.text()).then(svg => { z.orbit = svg; if (z.loading) z.loading.innerHTML = svg; }).catch(error => console.error('Failed to load orbit.svg:', error));
// The UI icons ship with the build (src/data/icons.json, maintained in the repo, not the database): a chunk of
// their own, named by content, so a browser keeps it until an icon actually changes. Started here, at load, so
// it downloads alongside everything else; Auth.open waits for it where it used to read /svg/icons.
const iconsReady = import('./data/icons.json').then((module) => module.default);

class Auth {
  constructor() {
    this.event = [];
    this.open();
  }

  async open() { try {
    // Offline mode: hydrate z from apps.json and stub identity BEFORE any
    // safeGet calls below — every subsequent get('/codes/...') etc. then
    // resolves locally via the offline wrapper. No-op when normal.
    if (OFFLINE) await bootOffline(z);
    this.code = code;
    if (this.code === 'play') {
      Game = (await import(`./game`)).Game;
      write(z, ['style', 'game'], document.createElement('style'));
      z.style.game.classList.add('style', 'game');
      z.style.game.textContent = (await import('./game.css')).default;
      document.head.appendChild(z.style.game);
    } else if (this.code === 'preview') {
      let previewDiv = document.createElement('div');
      previewDiv.classList.add('ui');
      previewDiv.style.paddingTop = '6px';
      previewDiv.innerHTML = '';
      document.body.appendChild(previewDiv);
      return;
    } else if (this.code === 'bonus') {
      this.app = 'math';
    } else if (this.code) {
      this.app = await get(`/codes/${this.code}/app`, { static: true });
      await get(`/apps/${this.app}/${this.code}`, { static: true });
    }
    if (this.app) write(z, ['app'], this.app);
    write(z, ['style', 'index'], style);
    write(z, ['svg', 'icons'], await iconsReady);
    if (this.code) write(z, ['teacher'], this.code === 'bonus' ? '2d9vML81TLYDkfvFl8fGlSwsxtf1' : await get(`/codes/${this.code}/teacher`, { static: true }));
    if (z.teacher) onValue(`/teachers/${z.teacher}/models`, () => {});
    document.addEventListener('contextmenu', (event) => event.preventDefault());
    document.addEventListener('keydown', (event) => {
      // Enter moves on to the next answer box as Tab would; on the canvas or the notes page it is
      // the canvas's own key (a new line of text, the next row of a template), and the Tab sent
      // after it moved a table's box one to the right after every Enter (logged live, Oct 2026).
      if (event.target?.classList?.contains('canvas') || event.target?.classList?.contains('notes-page')) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        let tab = new KeyboardEvent('keydown', { key: 'Tab', code: 'Tab', which: 9, keyCode: 9, bubbles: true, cancelable: true });
        if (event.touch) tab.touch = true;
        document.dispatchEvent(tab);
      }
    });
    document.addEventListener('touchmove', (event) => {
      if (event.touches.length > 1) event.preventDefault();
      // Skip the scroll-lock preventDefault on buttons/form controls. Apple Pencil
      // registers sub-pixel movement during a tap (a finger doesn't), firing touchmove
      // between touchstart and touchend — and iOS suppresses the synthesized click
      // when any touchmove in the sequence was preventDefault'd. Buttons are point
      // targets, so there's nothing meaningful to scroll on them anyway.
      if (event.target.closest('button, input, select, textarea, [role="button"]')) return;
      let scrollable;
      document.querySelectorAll('.scroll').forEach((element) => {
        if (element.contains(event.target)) {
          scrollable = element;
          return;
        }
      });
      if (!scrollable) event.preventDefault();
    }, { passive: false });
    document.ondblclick = (event) => event.preventDefault();
    // Firebase restores a saved login from the browser asynchronously, so auth.currentUser is null
    // for a moment after load. Without this wait a page with no code (the teacher dashboard) read
    // that moment as signed out and showed the login page on every refresh.
    if (!OFFLINE) await auth.authStateReady();
    if (OFFLINE) {
      // Offline mode: identity is already stubbed in z by bootOffline above —
      // skip the student-form / auth flow entirely and open the app directly.
      // Checked BEFORE the hash branch because `#offline` would otherwise enter
      // the login-hash check and fall through silently (no sub-branch matches
      // and the `else if (OFFLINE)` below would never be reached).
      // changeBackground() applies the per-app theme color to z.background —
      // jeopardy uses properties.background (#0f0f4d), math uses a student-color
      // radial gradient. Without it, #background stays transparent and only the
      // body's dark default shows.
      this.changeBackground();
      this.openApp();
    } else if (hash) {
      let login = hash.split('-');
      if (login[0] === 'enroll') {
        this.enrolling = await getDialog(true, 'Hello!', 'Is this your first time on Zebula?', 'Yes', null, 'No');
        this.load();
      } else if (login.length === 3) {
        // Hash form is lowercase-with-underscores (line 425 below: `${z.name.toLowerCase().replace(/\s/g, '_')}-...`).
        // Restore spaces and title-case so display contexts ("Briffo's Avatar", "Gunner has...") render correctly;
        // the lowercase form is reconstructed on demand for the email concat in connectStudent.
        write(z, ['name'], titleCase(login[0].replace(/_/g, ' ')));
        write(z, ['color'], { name: login[1], value: colors.find((color) => color.name === login[1]).value, mid: colors.find((color) => color.name === login[1]).mid, dark: colors.find((color) => color.name === login[1]).dark, darker: colors.find((color) => color.name === login[1]).darker, darkest: colors.find((color) => color.name === login[1]).darkest, neighbor: colors.find((color) => color.name === login[1]).neighbor, blend: colors.find((color) => color.name === login[1]).blend });
        for (let key of Object.keys(z.color)) document.documentElement.style.setProperty(`--student-color-${key}`, z.color[key]);
        write(z, ['emoji'], { name: login[2] });
        this.changeBackground();
        this.connectStudent();
      }
    } else if (auth.currentUser && !auth.currentUser.email.endsWith('@zebula.net') && await get(`/teachers/${auth.currentUser.uid}/email`) === auth.currentUser.email) {
      // Teacher already signed in from previous session — go straight to dashboard
      this.openDash();
    } else {
      // A student tab keeps its sign-in. It is shared by every tab in the browser, so signing out here
      // signed the student out of the lesson open in their other tabs and refused its writes (Oct 2026).
      // With a code they go straight back into it; without one the login page shows and a new sign-in
      // replaces this one. A teacher tab still drops a stray student login (from before the two apps).
      if (window.firebaseApp === apps.student && await this.redirectStudent()) return;
      if (auth.currentUser && window.firebaseApp !== apps.student) await auth.signOut();
      this.load();
    }
  } catch (error) { logError(error, 'opening the initial authorization page'); }}

  async load() {
    this.page = document.createElement('div');
    this.page.id = 'page';
    this.body = document.createElement('div');
    this.body.id = 'body';
    this.page.appendChild(this.body);
    document.body.appendChild(this.page);
    if (this.code) await this.openStudentForm();
    else {
      await this.openHome();
      z.background.style.opacity = 1;
      this.twinkle();
    }
    await this.openFooter();
    this.event.forEach((event) => event.element.addEventListener(event.type, event.function));
    if (!Game) {
      App = (await import(`./app`)).App;
      write(z, ['style', 'app'], document.createElement('style'));
      z.style.app.classList.add('style', 'app');
      z.style.app.textContent = (await import('./app.css')).default;
      document.head.appendChild(z.style.app);
    }
  }

  async openStudentForm() {
    this.studentForm = document.createElement('div');
    this.studentForm.classList.add('ui');
    this.studentForm.id = 'student-form';
    this.studentForm.appendChild(getHeader('Student', this.studentForm));

    this.nameInput = document.createElement('input');
    this.nameInput.classList.add('name-input');
    this.nameInput.placeholder = 'First Name';
    this.studentForm.appendChild(this.nameInput);
    if (hash === 'enroll' && this.enrolling === 1) {
      let nameNote = document.createElement('div');
      nameNote.classList.add('name-note');
      nameNote.innerHTML = 'Use the same name as your Outschool name.';
      this.studentForm.appendChild(nameNote);
    }

    this.colorTable = document.createElement('table');
    let colorButton = [];
    let row;
    for (let i = 0; i < colors.length - 1; i++) {
      if (i % 3 === 0) row = this.colorTable.insertRow();
      let cell = row.insertCell();
      colorButton[i] = document.createElement('button');
      colorButton[i].classList.add('color-button');
      colorButton[i].innerHTML = `${colors[i].name.charAt(0).toUpperCase()}${colors[i].name.slice(1)}`;
      colorButton[i].setAttribute('color-name', colors[i].name);
      colorButton[i].setAttribute('color-value', colors[i].value);
      colorButton[i].setAttribute('color-mid', colors[i].mid);
      colorButton[i].setAttribute('color-dark', colors[i].dark);
      colorButton[i].setAttribute('color-darker', colors[i].darker);
      colorButton[i].setAttribute('color-darkest', colors[i].darkest);
      colorButton[i].setAttribute('color-neighbor', colors[i].neighbor);
      colorButton[i].setAttribute('color-blend', colors[i].blend);
      colorButton[i].style.background = `linear-gradient(to bottom, ${colors[i].value} -200%, ${colors[i].dark} 150%)`;
      colorButton[i].style.textShadow = `1px 1px ${colors[i].darker}`;
      colorButton[i].addEventListener('click', (event) => this.colorButtonClick(event));
      colorButton[i].addEventListener('mouseover', (event) => this.changeBackground(event));
      cell.appendChild(colorButton[i]);
    }
    this.studentForm.appendChild(this.colorTable);

    this.emojiTable = document.createElement('table');
    this.emojiTable.id = 'emoji-table';
    let emojiButton = [];
    for (let i = 0; i < emojis.length - 1; i++) {
      if (i % 5 === 0) row = this.emojiTable.insertRow();
      let cell = row.insertCell();
      emojiButton[i] = document.createElement('button');
      emojiButton[i].classList.add('emoji-button');
      emojiButton[i].innerHTML = z.svg.icons[emojis[i].name];
      emojiButton[i].setAttribute('emoji-name', emojis[i].name);
      emojiButton[i].addEventListener('click', (event) => this.emojiButtonClick(event));
      cell.appendChild(emojiButton[i]);
    }
    this.studentForm.appendChild(this.emojiTable);
    this.body.appendChild(this.studentForm);
    if (code && code !== 'bonus' && !z.apps?.[this.app]?.[this.code] && !Game) this.oops('Invalid code');
  }

  async colorButtonClick(event) {
    let colorButton = event.currentTarget;
    let inputName = this.nameInput.value;
    if (inputName === '' || this.checking) return;
    this.colorSelected = true;

    // The checkLogin cloud function answers both questions this page needs (a pending student by this name, and
    // the emojis already taken by this name and colour), so the page never reads the roster, which holds every
    // student's login. A failed call asks nothing and greys out nothing; the sign-in itself still works.
    // The page's own loading circle covers the wait (a cold function can take a few seconds), with the panel
    // hidden, and `checking` keeps a second colour click from starting another check meanwhile.
    // The picked colour goes on first: the loading circle spins in --student-color-value.
    write(z, ['color'], { name: colorButton.getAttribute('color-name'), value: colorButton.getAttribute('color-value'), mid: colorButton.getAttribute('color-mid'), dark: colorButton.getAttribute('color-dark'), darker: colorButton.getAttribute('color-darker'), darkest: colorButton.getAttribute('color-darkest'), neighbor: colorButton.getAttribute('color-neighbor'), blend: colorButton.getAttribute('color-blend') });
    for (let key of Object.keys(z.color)) document.documentElement.style.setProperty(`--student-color-${key}`, z.color[key]);
    this.checking = true;
    this.studentForm.style.display = 'none';
    z.loading.style.display = '';
    z.loading.style.opacity = 1;
    let check = await httpsCallable(window.firebaseFunctions, 'checkLogin')({ teacher: z.teacher, name: inputName, color: colorButton.getAttribute('color-name') })
      .then((result) => result.data)
      .catch((error) => { logError(error, 'checking a login'); return { pending: false, taken: [] }; })
      .finally(() => {
        z.loading.style.opacity = 0;
        z.loading.style.display = 'none';
        this.checking = false;
      });
    this.studentForm.style.display = 'flex';
    if (!this.enrolling && check.pending) {
      this.studentForm.style.display = 'none';
      this.enrolling = await getDialog(true, 'Hello!', 'Is this your first time on Zebula?', 'Yes', null, 'No');
      if (this.enrolling === 1) this.enrolling = 0.5;
      this.studentForm.style.display = 'flex';
    }

    this.colorTable.style.display = 'none';

    this.studentForm.style.display = 'none';
    if ([0.5, 1].includes(this.enrolling)) for (let emoji of check.taken || []) {
      let button = document.querySelector(`button[emoji-name="${emoji}"]`);
      if (button) button.disabled = true;
    }
    this.setEmojiStep(true);
    this.emojiTable.style.display = 'table';
    this.studentForm.style.display = 'flex';
  }

  // The emoji step holds the name chosen at the colour step: it can't be edited there (the checks above were
  // made for that name), and the name note belongs to typing the name, so it hides. Back at the colour step
  // (after a failed login) both return.
  setEmojiStep(on) {
    this.nameInput.disabled = on;
    let note = this.studentForm.querySelector('.name-note');
    if (note) note.style.display = on ? 'none' : '';
  }

  changeBackground(event) {
    if (this.colorSelected) return;
    let themeColor;
    if (this.nameInput && this.nameInput.value === '') return;
    if (z.apps?.[this.app]?.[this.code]?.properties?.background) {
      themeColor = z.apps[this.app][this.code].properties.background;
      z.background.style.background = themeColor;
    } else {
      themeColor = event ? event.currentTarget.getAttribute('color-darkest') : z.color.darkest;
      z.background.style.background = `radial-gradient(circle at 50% -50%, rgba(0, 0, 0, 0) 0%, ${themeColor} 100%), radial-gradient(circle at 50% -50%, rgba(0, 0, 0, 0) ${event ? event.currentTarget.getAttribute('color-blend') : z.color.blend}, ${event ? event.currentTarget.getAttribute('color-neighbor') : z.color.neighbor} 300%)`;
      z.background.style.backgroundColor = event ? event.currentTarget.getAttribute('color-dark') : z.color.dark;
    }
    if (z.apps?.[this.app]?.[this.code]?.properties?.filter) {
      z.background.style.opacity = 1;
      this[z.apps[this.app][this.code].properties.filter]();
    }
  }

  emojiButtonClick(event) {
    write(z, ['emoji', 'name'], event.currentTarget.getAttribute('emoji-name'));
    this.emojiTable.style.display = 'none';
    this.studentForm.style.display = 'none';
    this.colorTable.style.display = 'table';
    this.setEmojiStep(false);
    this.studentAuth();
  }

  async studentAuth() {
    // Every student sign-in and sign-up runs on the student app, so it never replaces a teacher's
    // saved login in the same browser (see useApp).
    useApp('student');
    if (this.enrolling === 0.5) {
      await this.connectNewStudent();
      return;
    } else if (this.enrolling === 1) {
      // Outside the try below, a sign-up error used to stop the page with the panel hidden: nothing came back.
      try {
        await this.addStudent();
      } catch (error) {
        logError(error, 'adding a student');
        this.fail(error.message);
        return;
      }
    }
    try {
      await this.connectStudent();
    } catch (error) {
      // A mistyped name/color/emoji is an expected outcome here, not a fault — the flow
      // below already recovers (new-student path) and fail() writes a readable `connection`
      // entry naming what was attempted. Logging the raw auth rejection too filled the
      // teacher's log with red errors on every typo, burying real ones.
      const expected = ['auth/user-not-found', 'auth/wrong-password', 'auth/invalid-credential', 'auth/invalid-email'];
      if (!expected.some(code => error?.code === code || error?.message?.includes(code))) logError(error, 'connecting a student');
      try {
        if (this.enrolling === 2) this.fail('Invalid login after indicating not enrolling');
        else await this.connectNewStudent();
      } catch (error) {
        this.fail(error.message);
      }
    }
  }

  async addStudent() {
		// Student-initiated signup. Two normalized forms:
		//   - displayName (Title Case, e.g. "Suzie", "Ha Jin") — stored in the
		//     roster's `name` field; surfaces in nametags, logs, dialogs.
		//   - emailName (all lowercase, spaces → underscores) — used to build the
		//     auth email so "SUZIE", "suzie", "Suzie" all resolve to one account.
		let displayName = titleCase(this.nameInput.value.trim());
		let emailName = displayName.toLowerCase().replace(/ +/g, '_');
		let password = `.${z.color.name}.${z.emoji.name}.`;
		let email = `${emailName}${password}${z.teacher}@zebula.net`;
		let userCredential;
		try {
			userCredential = await createUserWithEmailAndPassword(auth, email, password);
		} catch (error) {
			if (error?.code !== 'auth/email-already-in-use') throw error;
			// This login already exists, so sign in with it (the password is the colour and emoji just picked).
			// An account with a class record is simply a returning student and keeps its record untouched, so a
			// teacher's settings are never overwritten. One without (left behind when its record was removed, or
			// whose emoji the page could not grey out) is set up below like a new sign-up.
			userCredential = await signInWithEmailAndPassword(auth, email, password);
			if (await get(`/teachers/${z.teacher}/roster/${userCredential.user.uid}`, { cache: false })) return;
		}
    await update(`/students/${userCredential.user.uid}`, { settings: { log: true, stats: true }, teacher: z.teacher });
    await update(`/teachers/${z.teacher}/roster/${userCredential.user.uid}`, { color: z.color, emoji: z.emoji, grade: codeGrade(code), name: displayName, status: 'active', type: 'async' });
    await update(`/teachers/${z.teacher}/new`, { [userCredential.user.uid]: null });
    await getDialog(true, 'Welcome!', `Here's the login you chose:<div style="font-size:15px;padding:5px 0px">First Name: ${displayName}<br>Color: ${z.color.name.charAt(0).toUpperCase()}${z.color.name.slice(1)}<br>Emoji: ${z.emoji.name.charAt(0).toUpperCase()}${z.emoji.name.slice(1)}</div>Be sure to write this down!<br>Use the same login from now on.`, 'Got it!');
	}

  async connectStudent() {
    // Same normalization split as addStudent: Title Case stored, lowercase emailed.
    let displayName = z.name || titleCase(this.nameInput.value.trim());
    let emailName = displayName.toLowerCase().replace(/ +/g, '_');
    let password = `.${z.color.name}.${z.emoji.name}.`;
    let email = `${emailName}${password}${z.teacher}@zebula.net`;
    await signInWithEmailAndPassword(auth, email, password);
    write(z, ['student'], auth.currentUser.uid);
    write(z, ['uid'], z.student);
    write(z, ['name'], displayName);
    update(`/teachers/${z.teacher}/roster/${z.student}`, { color: z.color, emoji: z.emoji });
    if (this.enrolling === 1 || !hash || hash === 'enroll') {
      this.footer.style.opacity = 0;
      setTimeout(() => this.footer.remove(), 350);
      this.studentForm.remove();
    }
    this.openApp();
  }

  async connectNewStudent() {
    let displayName = titleCase(this.nameInput.value.trim());
    let emailName = displayName.toLowerCase().replace(/ +/g, '_');
    let password = '.white.new.';
    let email = `${emailName}${password}${z.teacher}@zebula.net`;
    await signInWithEmailAndPassword(auth, email, password);
    password = `.${z.color.name}.${z.emoji.name}.`;
    email = `${emailName}${password}${z.teacher}@zebula.net`;
    await updateEmail(auth.currentUser, email);
    await updatePassword(auth.currentUser, password);
    write(z, ['student'], auth.currentUser.uid);
    write(z, ['uid'], z.student);
    write(z, ['name'], displayName);
    await update(`/students/${z.student}`, { settings: { log: true, stats: true }, teacher: z.teacher });
    // Carry the teacher's chosen class type from the /new/ record (they set it when creating
    // the account) instead of forcing async — hardcoding 'async' here discarded a teacher's
    // 'sync' selection on the student's first login.
    let type = await get(`/teachers/${z.teacher}/new/${z.student}/type`) || 'async';
    // Same carry-over for the NAME. A teacher-created account keeps the capitalisation the
    // teacher typed — they wrote "AJ" or "McKay" deliberately — and titleCase() would flatten
    // it to "Aj" on the student's very first login, with no way back from the student's side.
    // Student-created accounts have no pending record, so they keep the titleCase default.
    let pendingName = await get(`/teachers/${z.teacher}/new/${z.student}/name`);
    if (pendingName) write(z, ['name'], pendingName);
    await update(`/teachers/${z.teacher}/roster/${z.student}`, { color: z.color, emoji: z.emoji, grade: codeGrade(code), name: z.name, status: 'active', type });
    await update(`/teachers/${z.teacher}/new`, { [z.student]: null });
    await getDialog(true, 'Welcome!', `Here's the login you chose:<div style="font-size:15px;padding:5px 0px">First Name: ${z.name}<br>Color: ${z.color.name.charAt(0).toUpperCase()}${z.color.name.slice(1)}<br>Emoji: ${z.emoji.name.charAt(0).toUpperCase()}${z.emoji.name.slice(1)}</div>Be sure to write this down!<br>Use the same login from now on.`, 'Got it!');
    this.footer.style.opacity = 0;
    setTimeout(() => this.footer.remove(), 350);
    this.studentForm.remove();
    this.openApp();
  }

  // A locked account may not reach ANY of the site. This sits in openApp because that is
  // the one choke point every student entry path funnels through — the login form
  // (connectStudent / connectNewStudent), the saved login hash, and redirectStudent — and
  // because it is also where the lesson (App) and the game (Game) branch, so one gate
  // covers both. Same reasoning as the teacher-vs-student guard living inside openDash
  // rather than at its call sites: put the check where the paths converge and every future
  // caller inherits it. Returns true if it took over the flow.
  async blockLocked() { try {
    // Offline mode has no roster to consult, and no z.teacher/z.student means there is no
    // account to be locked (the teacher flows never reach here).
    if (OFFLINE || !z.teacher || !z.student) return false;
    // The live listener below can re-fire (the sign-out alone churns it), and each pass
    // would stack another undismissable overlay on top of the last.
    if (this.locked) return true;
    let profile = await get(`/teachers/${z.teacher}/roster/${z.student}`) || await get(`/teachers/${z.teacher}/new/${z.student}`);
    if (!isLocked(profile)) return false;
    this.locked = true;
    logMessage(`${z.name} attempted to connect with the code ${this.code} but the account is locked.`, { app: this.app, code: this.code, student: z.student, tooltip: navigator.userAgent, type: 'connection', version: process.env.VERSION });
    // Sign out and drop the login hash BEFORE the dialog: the hash is what auto-connects on
    // the next load, so leaving it in place would spin the student through this same block
    // on every refresh, and leaving them signed in would let any later entry point that
    // trusts auth.currentUser (openDash's redirectStudent) hand them the app anyway.
    // First, while still signed in, flag the galaxy star so its name hides: a scheduled lock is
    // decided at read time and nothing else notices the date arriving. Only onto a star that
    // exists (an update would create a partial one).
    if (auth.currentUser && await get(`/teachers/${z.teacher}/game/galaxy/${z.student}/star`, { cache: false })) await update(`/teachers/${z.teacher}/game/galaxy/${z.student}`, { inactive: true });
    if (auth.currentUser) await auth.signOut();
    window.location.hash = '';
    this.close();
    document.getElementById('page')?.remove();
    // No button, and `required` so the overlay can't be clicked away — there is nothing for
    // the student to do here, and a dismissible dialog would leave them on a blank page
    // wondering whether it was a glitch.
    getDialog(true, 'Locked', `This account is locked.<br>Please talk to your teacher.`);
    return true;
  } catch (error) { logError(error, 'checking whether a student account is locked'); return false; } }

  async openApp() {
    if (await this.blockLocked()) return;
    if (!App && !Game) {
      App = (await import(`./app`)).App;
      write(z, ['style', 'app'], document.createElement('style'));
      z.style.app.classList.add('style', 'app');
      z.style.app.textContent = (await import(`./app.css`)).default;
      document.head.appendChild(z.style.app);
    }
    if (App) new App(this.app, this.code, z.color, document, z.emoji, z.name, null, z.student, window);
    else if (Game) {
      let game = new Game(z.color, document, z.emoji, z.name, null, null, z.student, window);
      game.connect();
    }
    // A lock applied while the student is already inside takes effect on the spot. The
    // moment a teacher reaches for this is usually mid-class, and a lock that only bit at
    // the next login would look broken to them. Status only — a scheduled date arriving
    // mid-session would be a midnight edge case, and the entry gate catches it next time.
    if (!OFFLINE && z.teacher && z.student) onValue(`/teachers/${z.teacher}/roster/${z.student}/status`, (snapshot) => {
      if (snapshot?.val() === 'locked') this.blockLocked();
    });
    this.close();
    // In offline mode, keep `#offline` in the URL so reloads re-trigger
    // OFFLINE detection at module load. Otherwise the normal login-hash
    // form (`student-green-apple`) signals identity for re-auth flows.
    window.location.hash = OFFLINE ? 'offline' : `${z.name.toLowerCase().replace(/\s/g, '_')}-${z.color.name}-${z.emoji.name}`;
  }

  async fail(error) {
    this.colorSelected = false;
    this.failCount = this.failCount + 1 || 1;
    logMessage(`${this.nameInput.value} attempted to connect with the code ${this.code}, the color ${z.color.name}, and the ${z.emoji.name} emoji but encountered an error: ${error}`, { app: this.app, code: this.code, student: z.student, tooltip: navigator.userAgent, type: 'connection', version: process.env.VERSION });
    if (this.failCount < 5) {
      this.nameInput.placeholder = 'Try again';
      // Only this prompt is gated. The other two are not failure-driven: the one on entry is
      // a deliberate choice, and the name-match one claims an account the TEACHER already
      // created rather than making a new one. Fetched rather than read from z, because a
      // student on the login screen has no reason to have the teacher's settings cached yet.
      // Absent or true both allow it - only an explicit false, set by the teacher in their
      // own settings, turns it off.
      let enroll = await get(`/teachers/${z.teacher}/settings/enroll`);
      if (enroll !== false && !this.enrolling && this.failCount === 2) {
        this.enrolling = await getDialog(true, 'Hello!', 'Is this your first time on Zebula?', 'Yes', null, 'No');
        if (this.enrolling === 1) {
          this.studentAuth();
          return;
        }
      }
    } else this.oops('Too many tries');
    this.nameInput.value = '';
    this.studentForm.style.display = 'flex';
  }

  oops(error) {
    this.nameInput.classList.add('error');
    this.nameInput.disabled = true;
    this.nameInput.value = '';
    this.nameInput.placeholder = error;
    this.colorTable.style.display = 'none';
    this.studentForm.querySelector('.header-center').innerHTML = 'Oops...';
    this.studentForm.style.display = 'flex';
  }

  async openHome(user) {
    this.home = document.createElement('div');
    this.home.id = 'home';
    let homeBody = document.createElement('div');
    homeBody.classList.add('scroll', 'top');
    homeBody.id = 'home-body';
    homeBody.style.setProperty('--scroll', 0);
    homeBody.addEventListener('scroll', () => homeBody.style.setProperty('--scroll', Math.min(homeBody.scrollTop / (homeBody.scrollHeight - homeBody.clientHeight), 1)));

    let homeVideo = document.createElement('video');
    homeVideo.id = 'home-video';
    homeVideo.controls = true;
    homeVideo.disablePictureInPicture = true;
    homeVideo.disableRemotePlayback = true;
    homeVideo.setAttribute('controlsList', 'nodownload noplaybackrate');
    homeVideo.src = 'https://firebasestorage.googleapis.com/v0/b/zebula.appspot.com/o/video%2Fvideo.mp4?alt=media&token=e5e07dab-587b-4b94-96ed-6544f05fd794';
    homeVideo.poster = 'https://firebasestorage.googleapis.com/v0/b/zebula.appspot.com/o/video%2Fposter.png?alt=media&token=f94b2706-7039-4f63-bc72-caf85eecd946';
    homeBody.appendChild(homeVideo);

    let homeAbout = document.createElement('div');
    homeAbout.classList.add('ui');
    homeAbout.id = 'home-about';
    homeAbout.appendChild(getHeader('https://firebasestorage.googleapis.com/v0/b/zebula.appspot.com/o/images%2Fteacher.jpg?alt=media&token=cc6dfe24-c805-4545-9485-31d43f649d7a', homeAbout));
    let homeAboutText = document.createElement('div');
    homeAboutText.classList.add('text');
    homeAboutText.innerHTML = 'Zebula is a learning platform for math grades 5-8, with live classes hosted by Mr. Rieth.<br>Mr. Rieth has a degree in elementary education and five years of experience teaching online.<br><button type="button" onclick="window.open(\'https://outschool.com/teachers/rieth\', \'_blank\')" style="font-size:14px;height:36px;margin-top:8px;padding:6px 9px">Mr. Rieth\'s Outschool Profile & Reviews</button>';
    homeAboutText.style.marginBottom = '0px';
    homeAbout.appendChild(homeAboutText);
    homeBody.appendChild(homeAbout);

    let homeClasses = document.createElement('div');
    homeClasses.classList.add('ui');
    homeClasses.id = 'home-classes';
    homeClasses.appendChild(getHeader('Classes', homeClasses));
    let homeClassesText = document.createElement('div');
    homeClassesText.classList.add('text');
    homeClassesText.innerHTML = 'Mr. Rieth\'s math classes feature 30 minute sessions that are held three days per week.';
    homeClasses.appendChild(homeClassesText);
    let homeClassesTabs = document.createElement('div');
    homeClassesTabs.id = 'home-classes-tabs';
    let homeClassesTabMondayButton = document.createElement('button');
    homeClassesTabMondayButton.innerHTML = 'Monday';
    homeClassesTabs.appendChild(homeClassesTabMondayButton);
    let homeClassesTabWednesdayButton = document.createElement('button');
    homeClassesTabWednesdayButton.innerHTML = 'Wednesday';
    homeClassesTabs.appendChild(homeClassesTabWednesdayButton);
    let homeClassesTabFridayButton = document.createElement('button');
    homeClassesTabFridayButton.innerHTML = 'Friday';
    homeClassesTabs.appendChild(homeClassesTabFridayButton);
    homeClasses.appendChild(homeClassesTabs);
    let days = { Monday: 'The Monday session is when the teacher provides instruction on the weekly topic. All topics are taught in complete detail without any assumptions of prior knowledge. Following the lesson, the students begin guided practice.', Wednesday: 'For the Wednesday session, students will continue practicing the topic at either a basic or advanced level, depending upon their performance over the course of the week. The teacher will differentiate instruction by scaffolding as much or as little as necessary as students build independence with the learning objective.', Friday: 'The Friday session caps off the week by engaging students in a Jeopardy-style math review that supports retention of the skills learned in this course. Each student plays their own personalized game, and the teacher will prioritize student understanding by taking the time to discuss challenging questions.' };
    let homeClassesDiv = document.createElement('div');
    homeClassesDiv.id = 'home-classes-div';
    homeClassesTabMondayButton.addEventListener('click', () => {
      homeClassesTabs.querySelectorAll('button').forEach((button) => button.classList.remove('selected'));
      homeClassesTabMondayButton.classList.add('selected');
      homeClassesDiv.innerHTML = days['Monday'];
      homeClassesDiv.style.borderRadius = '0px 4px 4px 4px';
    });
    homeClassesTabMondayButton.click();
    homeClassesTabWednesdayButton.addEventListener('click', () => {
      homeClassesTabs.querySelectorAll('button').forEach((button) => button.classList.remove('selected'));
      homeClassesTabWednesdayButton.classList.add('selected');
      homeClassesDiv.innerHTML = days['Wednesday'];
      homeClassesDiv.style.borderRadius = '4px';
    });
    homeClassesTabFridayButton.addEventListener('click', () => {
      homeClassesTabs.querySelectorAll('button').forEach((button) => button.classList.remove('selected'));
      homeClassesTabFridayButton.classList.add('selected');
      homeClassesDiv.innerHTML = days['Friday'];
      homeClassesDiv.style.borderRadius = '4px 0px 4px 4px';
    });
    homeClasses.appendChild(homeClassesDiv);
    homeBody.appendChild(homeClasses);

    let homeCurriculum = document.createElement('div');
    homeCurriculum.classList.add('ui');
    homeCurriculum.id = 'home-curriculum';
    homeCurriculum.appendChild(getHeader('Curriculum', homeCurriculum));
    let homeCurriculumText = document.createElement('div');
    homeCurriculumText.classList.add('text');
    homeCurriculumText.innerHTML = 'Zebula\'s 5-8 math curriculum is based on the US Common Core State Standards.';
    homeCurriculum.appendChild(homeCurriculumText);
    let homeCurriculumTabs = document.createElement('div');
    homeCurriculumTabs.id = 'home-curriculum-tabs';
    let homeCurriculumTab5Button = document.createElement('button');
    homeCurriculumTab5Button.innerHTML = 'Grade 5';
    homeCurriculumTabs.appendChild(homeCurriculumTab5Button);
    let homeCurriculumTab6Button = document.createElement('button');
    homeCurriculumTab6Button.innerHTML = 'Grade 6';
    homeCurriculumTabs.appendChild(homeCurriculumTab6Button);
    let homeCurriculumTab7Button = document.createElement('button');
    homeCurriculumTab7Button.innerHTML = 'Grade 7';
    homeCurriculumTabs.appendChild(homeCurriculumTab7Button);
    let homeCurriculumTab8Button = document.createElement('button');
    homeCurriculumTab8Button.innerHTML = 'Grade 8';
    homeCurriculumTabs.appendChild(homeCurriculumTab8Button);
    homeCurriculum.appendChild(homeCurriculumTabs);
    let homeCurriculumDiv = document.createElement('div');
    homeCurriculumDiv.id = 'home-curriculum-div';
    let topicsTable = document.createElement('table');
    topicsTable.id = 'topics-table';
    homeCurriculumTab5Button.addEventListener('click', () => {
      homeCurriculumTabs.querySelectorAll('button').forEach((button) => button.classList.remove('selected'));
      homeCurriculumTab5Button.classList.add('selected');
      topicsTable.innerHTML = '';
      for (let topic of topics[5]) {
        let row = topicsTable.insertRow();
        let cell = row.insertCell();
        cell.innerHTML = topic;
      }
      homeCurriculumDiv.style.borderRadius = '0px 4px 4px 4px';
    });
    homeCurriculumTab5Button.click();
    homeCurriculumTab6Button.addEventListener('click', () => {
      homeCurriculumTabs.querySelectorAll('button').forEach((button) => button.classList.remove('selected'));
      homeCurriculumTab6Button.classList.add('selected');
      topicsTable.innerHTML = '';
      for (let topic of topics[6]) {
        let row = topicsTable.insertRow();
        let cell = row.insertCell();
        cell.innerHTML = topic;
      }
      homeCurriculumDiv.style.borderRadius = '4px';
    });
    homeCurriculumTab7Button.addEventListener('click', () => {
      homeCurriculumTabs.querySelectorAll('button').forEach((button) => button.classList.remove('selected'));
      homeCurriculumTab7Button.classList.add('selected');
      topicsTable.innerHTML = '';
      for (let topic of topics[7]) {
        let row = topicsTable.insertRow();
        let cell = row.insertCell();
        cell.innerHTML = topic;
      }
      homeCurriculumDiv.style.borderRadius = '4px';
    });
    homeCurriculumTab8Button.addEventListener('click', () => {
      homeCurriculumTabs.querySelectorAll('button').forEach((button) => button.classList.remove('selected'));
      homeCurriculumTab8Button.classList.add('selected');
      topicsTable.innerHTML = '';
      for (let topic of topics[8]) {
        let row = topicsTable.insertRow();
        let cell = row.insertCell();
        cell.innerHTML = topic;
      }
      homeCurriculumDiv.style.borderRadius = '4px 0px 4px 4px';
    });
    homeCurriculumDiv.appendChild(topicsTable);
    homeCurriculum.appendChild(homeCurriculumDiv);
    homeBody.appendChild(homeCurriculum);
    this.home.appendChild(homeBody);

    /*let homeFooter = document.createElement('div');
    homeFooter.classList.add('ui');
    homeFooter.id = 'home-footer';
    let viewScheduleButton = document.createElement('button');
    viewScheduleButton.innerHTML = 'Schedule';
    homeFooter.appendChild(viewScheduleButton);
    viewScheduleButton.addEventListener('click', () => {
      z.loading.style.display = '';
      z.loading.style.opacity = 1;
      this.home.remove();
      this.openSchedule(user);
    });
    if (user) {
      let subscriptionsFormButton = document.createElement('button');
      subscriptionsFormButton.id = 'subscriptions-form-button';
      subscriptionsFormButton.innerHTML = 'Subscriptions';
      homeFooter.appendChild(subscriptionsFormButton);
      subscriptionsFormButton.addEventListener('click', () => {
        z.loading.style.display = '';
        z.loading.style.opacity = 1;
        this.home.remove();
        this.openSubscriptionsForm(plans, user);
      });
    } else {
      let loginRegisterButton = document.createElement('button');
      loginRegisterButton.innerHTML = 'Log In/Register';
      homeFooter.appendChild(loginRegisterButton);
      loginRegisterButton.addEventListener('click', () => {
        z.loading.style.display = '';
        z.loading.style.opacity = 1;
        this.home.remove();
        this.openParentForm();
      });
    }
    this.home.appendChild(homeFooter);*/
    this.body.appendChild(this.home);
    z.loading.style.opacity = 0;
    z.loading.style.display = 'none';
    this.lastOpened = 'home';
  }

  async openParentForm() {
    this.parentForm = document.createElement('div');
    this.parentForm.classList.add('ui');
    this.parentForm.id = 'parent-form';
    this.parentForm.appendChild(getHeader('Parent', this.parentForm));

    let emailInput = document.createElement('input');
    emailInput.classList.add('email-input');
    emailInput.placeholder = 'Email';
    emailInput.type = 'email';
    this.parentForm.appendChild(emailInput);

    let passwordInput = document.createElement('input');
    passwordInput.classList.add('password-input');
    passwordInput.placeholder = 'Password';
    passwordInput.type = 'password';
    this.parentForm.appendChild(passwordInput);

    let logInButton = document.createElement('button');
    logInButton.classList.add('log-in-button');
    logInButton.innerHTML = 'Log In';
    logInButton.addEventListener('click', () => this.connectParent(emailInput.value, passwordInput.value));
    this.parentForm.appendChild(logInButton);

    let registerFormButton = document.createElement('button');
    registerFormButton.classList.add('register-form-button');
    registerFormButton.innerHTML = 'Register';
    registerFormButton.addEventListener('click', () => {
      if (emailInput.value) this.emailInput = emailInput.value;
      if (passwordInput.value) this.passwordInput = passwordInput.value;
      this.parentForm.remove();
      this.openParentRegisterForm();
    });
    this.parentForm.appendChild(registerFormButton);

    let scheduleFormButton = document.createElement('button');
    scheduleFormButton.id = 'schedule-form-button';
    scheduleFormButton.innerHTML = !this.lastOpened || this.lastOpened === 'home' ? 'Back to Home' : 'Back to Schedule';
    scheduleFormButton.addEventListener('click', () => {
      this.parentForm.remove();
      if (!this.lastOpened || this.lastOpened === 'home') this.openHome();
      else if (this.lastOpened === 'schedule') this.openSchedule();
    });
    this.parentForm.appendChild(scheduleFormButton);

    this.body.appendChild(this.parentForm);
  }

  async connectParent(email, password) {
    useApp('teacher');
    this.parentForm.remove();
    await signInWithEmailAndPassword(auth, email, password);
    this.footerHomeButton.disabled = true;
    this.footerRoleButton.style.opacity = 0;
    setTimeout(() => this.footerRoleButton.remove(), 350);
    if (!this.lastOpened || this.lastOpened === 'home') this.openHome(auth.currentUser);
        else if (this.lastOpened === 'schedule') this.openSchedule(auth.currentUser);
  }

  async openParentRegisterForm() {
    this.parentRegisterForm = document.createElement('div');
    this.parentRegisterForm.classList.add('ui');
    this.parentRegisterForm.id = 'parent-register-form';
    this.parentRegisterForm.appendChild(getHeader('Parent', this.parentRegisterForm));
  
    let parentNameInput = document.createElement('input');
    parentNameInput.placeholder = 'Name';
    this.parentRegisterForm.appendChild(parentNameInput);
  
    let emailInput = document.createElement('input');
    emailInput.classList.add('email-input');
    emailInput.placeholder = 'Email';
    emailInput.type = 'email';
    if (this.emailInput) emailInput.value = this.emailInput;
    this.parentRegisterForm.appendChild(emailInput);
  
    let passwordInput = document.createElement('input');
    passwordInput.classList.add('password-input');
    passwordInput.placeholder = 'Password';
    passwordInput.type = 'password';
    if (this.passwordInput) passwordInput.value = this.passwordInput;
    this.parentRegisterForm.appendChild(passwordInput);
  
    let backButton = document.createElement('button');
    backButton.classList.add('back-button');
    backButton.innerHTML = 'Back';
    backButton.addEventListener('click', () => {
      this.parentRegisterForm.remove();
      this.openParentForm();
    });
    this.parentRegisterForm.appendChild(backButton);

    let registerButton = document.createElement('button');
    registerButton.classList.add('register-button');
    registerButton.innerHTML = 'Register';
    registerButton.addEventListener('click', () => {
      if (!parentNameInput.value || !emailInput.value || !passwordInput.value) return;
      this.parentRegisterForm.style.display = 'none';
      useApp('teacher');
      createUserWithEmailAndPassword(auth, emailInput.value, passwordInput.value).then(async (userCredential) => {
        updateProfile(userCredential.user, { displayName: parentNameInput.value });
        update(`/parents/${userCredential.user.uid}`, { name: parentNameInput.value, email: userCredential.user.email });
        await sendEmailVerification(userCredential.user);
        await getDialog(false, 'One more thing...', `Before you can subscribe to a class, you must verify your email.<br><br>If you do not see the verification email in your inbox, check your spam folder.<br><br>Once verified, refresh this page and log in again.`, 'Got it!');
        if (!this.lastOpened || this.lastOpened === 'home') this.openHome(userCredential.user);
        else if (this.lastOpened === 'schedule') this.openSchedule(userCredential.user);
      }).catch(async (error) => {
        this.parentRegisterForm.style.display = '';
        logError(error, 'registering a parent account');
        getDialog(false, 'Oops...', error.message || 'Registration failed. Please try again.', 'Got it!');
      });
    });
    this.parentRegisterForm.appendChild(registerButton);
    this.body.appendChild(this.parentRegisterForm);
  }

  async openSchedule(user) {
    this.schedule = document.createElement('div');
    this.schedule.id = 'schedule';
    let scheduleBody = document.createElement('div');
    scheduleBody.classList.add('ui');
    scheduleBody.id = 'schedule-body';
    scheduleBody.appendChild(getHeader('Schedule', scheduleBody));

    let scheduleInfoDiv = document.createElement('div');
    scheduleInfoDiv.id = 'schedule-info-div';
    scheduleInfoDiv.innerHTML = 'Subscriptions are $42 per week.<br>All dates and times are based on US Pacific Time.<br>Classes are held M/W/F except during ';
    let holidaysLink = document.createElement('a');
    holidaysLink.id = 'holidays-link';
    holidaysLink.innerHTML = 'holidays.';
    holidaysLink.addEventListener('click', () => getDialog(false, 'Holidays', 'No class on:<br>Memorial Day (US)<br>Labor Day (US)<br>A refund will be issued for the cost of one class.<br><br>No class for the full week of:<br>New Year\'s Day<br>Independence Day (US)<br>Thanksgiving (US)<br>Christmas<br>A refund will be issued for the cost of the full week.', 'Got it!'));
    scheduleBody.appendChild(scheduleInfoDiv);
    scheduleInfoDiv.appendChild(holidaysLink);
    let scheduleInfoDiv2 = document.createElement('div');
    scheduleInfoDiv2.id = 'schedule-info-div-2';
    scheduleInfoDiv2.innerHTML = 'Currently, grade 5 is taught via Zebula, and grades 6-8 are taught via Outschool, with plans to switch to Zebula in the future. Check availability of Outschool classes at the links below.';
    scheduleBody.appendChild(scheduleInfoDiv2);

    let [table, timeCell, timeDiv, timeRow, topicCell, topicDiv, topicRow, seatsCell, seatsDiv, subscribeCell, subscribeButton, subscribeFunction] = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}, {}];
    let finePrint = "When starting a subscription,<br>a charge of $42 will process after 2AM every Monday (Pacific) until the subscription is stopped.<br>To stop a subscription, log in<br>and click 'Subscriptions.'<br><br>No refunds will be issued with the exception of class holidays: Memorial Day (US) and Labor Day (US) and the full weeks of New Year's Day, Independence Day (US), Thanksgiving (US), and Christmas.<br><br>Only children are allowed in the online classroom, and only the registered child may attend.<br>Violation of these rules will result in removal with no refund.<br><br>If you agree to these terms, select a payment method below to subscribe.";
    let subs = await get(`/subs`);
    let seats = {};
    let topic = {};
    for (let sub in subs) seats[subs[sub].split('-')[1]] = Number(seats[subs[sub].split('-')[1]]) + 1 || 1;
    let previousGrade, previousTopic;
    let scheduleTabs = document.createElement('div');
    scheduleTabs.id = 'schedule-tabs';
    scheduleBody.appendChild(scheduleTabs);
    let sectionsDiv = document.createElement('div');
    sectionsDiv.id = 'sections-div';
    for (let plan in plans) {
      let grade = plans[plan].section.charAt(0);
      if (plans[plan].section.charAt(1) === 'A') {
        let scheduleTabButton = document.createElement('button');
        scheduleTabButton.innerHTML = `Grade ${grade}`;
        scheduleTabs.appendChild(scheduleTabButton);
        table[grade] = document.createElement('table');
        scheduleBody.appendChild(sectionsDiv);
        scheduleTabButton.addEventListener('click', () => {
          scheduleTabs.querySelectorAll('button').forEach((button) => button.classList.remove('selected'));
          scheduleTabButton.classList.add('selected');
          switch (grade) {
            case '5': sectionsDiv.style.borderRadius = '0px 4px 4px 4px'; break;
            case '6': sectionsDiv.style.borderRadius = '4px'; break;
            case '7': sectionsDiv.style.borderRadius = '4px'; break;
            case '8': sectionsDiv.style.borderRadius = '4px 0px 4px 4px'; break;
          }
          sectionsDiv.innerHTML = '';
          sectionsDiv.appendChild(table[grade]);
        });
        if (grade === '5') scheduleTabButton.click();
      }

      topic[plan] = await this.getClassTopic(plans[plan].hours, plans[plan].minutes);
      if (!(previousGrade && previousTopic && previousGrade === grade && previousTopic === topic[plan])) {
        topicRow[plan] = table[grade].insertRow();
        topicCell[plan] = topicRow[plan].insertCell();
        topicCell[plan].colSpan = 3;
        topicDiv[plan] = document.createElement('div');
        topicDiv[plan].id = 'topic-div';
        topicDiv[plan].innerHTML = topics[grade][topic[plan]];
        topicCell[plan].appendChild(topicDiv[plan]);
      }

      timeRow[plan] = table[grade].insertRow();
      let time = await this.getClassTime(plans[plan].hours, plans[plan].minutes);
      let formattedTime = `${plans[plan].time.split('–')[0]} ${plans[plan].time.split(' ')[1]} PT`;
      timeCell[plan] = timeRow[plan].insertCell();
      timeDiv[plan] = document.createElement('div');
      timeDiv[plan].id = 'time-div';
      timeDiv[plan].innerHTML = `Start ${time.date}<br>${plans[plan].time}`;
      timeCell[plan].appendChild(timeDiv[plan]);
      seatsCell[plan] = plan.charAt(0) === 'P' ? timeRow[plan].insertCell() : null;
      seatsDiv[plan] = document.createElement('div');
      seatsDiv[plan].id = 'seats-div';
      if (plan.charAt(0) === 'P') {
        seatsDiv[plan].innerHTML = `${z.svg.icons.bust}${seats[plans[plan].section] || 0}/8`;
        seatsDiv[plan].title = 'Occupied Seats';
        seatsCell[plan].appendChild(seatsDiv[plan]);
      }
      subscribeCell[plan] = timeRow[plan].insertCell();
      subscribeButton[plan] = document.createElement('button');
      if ((user && user.emailVerified) || plan.charAt(0) !== 'P') {
        subscribeButton[plan].innerHTML = plan.charAt(0) === 'P' ? 'Subscribe' : 'Enroll on Outschool';
        if (plan.charAt(0) !== 'P') subscribeButton[plan].style.width = '164px';
        subscribeCell[plan].appendChild(subscribeButton[plan]);
        if (plan.charAt(0) === 'P') {
          subscribeFunction[plan] = async (student) => {
            this.schedule.remove();
            let result = await getPaymentDialog(document, 'The fine print...', finePrint, user.uid, plan, plans[plan].section, student.replace(' ', '_'), time.utc);
            if (result === 'approved') {
              await update('/subs', { [`temp-${user.uid}`]: `${user.uid}-${plans[plan].section}-${student.replace(' ', '_')}` });
              await update(`/parents/${user.uid}/subs`, { [`temp-${user.uid}`]: `${user.uid}-${plans[plan].section}-${student.replace(' ', '_')}` });
              await getDialog(false, 'Success!', `${student} is now subscribed to the class at ${formattedTime}.`, 'Got it!');
              this.openSchedule(user);
            } else if (result === 'cancelled') this.openSchedule(user);
            else await getDialog(false, 'Hold up!', `There was an error creating the subscription.<br>For assistance, contact<br>learn@zebula.net`, 'Will do!');
          };
          subscribeButton[plan].addEventListener('click', async () => await getInputDialog(document, "Who's joining?", 'Enter the first name of the student joining the class.', 'First Name', subscribeFunction[plan]));
        } else subscribeButton[plan].addEventListener('click', () => window.open(plan, '_blank'));
      } else if (user && !user.emailVerified) {
        subscribeButton[plan].innerHTML = 'Verify Email';
        subscribeButton[plan].addEventListener('click', async () => await getDialog(false, 'One more thing...', `Before you can subscribe to a class, you must verify your email.<br><br>If you do not see the verification email in your inbox, check your spam folder.<br><br>Once verified, refresh this page and log in again.`, 'Got it!'));
      } else {
        subscribeButton[plan].innerHTML = 'Log In/Register';
        subscribeButton[plan].addEventListener('click', () => {
          this.schedule.remove();
          this.openParentForm();
        });
      }
      subscribeCell[plan].appendChild(subscribeButton[plan]);
      [previousGrade, previousTopic] = [grade, topic[plan]];
    }
    this.schedule.appendChild(scheduleBody);

    let scheduleFooter = document.createElement('div');
    scheduleFooter.classList.add('ui');
    scheduleFooter.id = 'schedule-footer';
    let homeButton = document.createElement('button');
    homeButton.id = 'home-button';
    homeButton.innerHTML = 'Home';
    scheduleBody.appendChild(homeButton);
    homeButton.addEventListener('click', () => {
      this.schedule.remove();
      this.openHome(user);
    });
    scheduleFooter.appendChild(homeButton);
    if (user) {
      let subscriptionsFormButton = document.createElement('button');
      subscriptionsFormButton.id = 'subscriptions-form-button';
      subscriptionsFormButton.innerHTML = 'Subscriptions';
      scheduleFooter.appendChild(subscriptionsFormButton);
      subscriptionsFormButton.addEventListener('click', () => {
        z.loading.style.display = '';
        z.loading.style.opacity = 1;
        this.schedule.remove();
        this.openSubscriptionsForm(plans, user);
      });
    } else {
      let loginRegisterButton = document.createElement('button');
      loginRegisterButton.innerHTML = 'Log In/Register';
      scheduleFooter.appendChild(loginRegisterButton);
      loginRegisterButton.addEventListener('click', () => {
        z.loading.style.display = '';
        z.loading.style.opacity = 1;
        this.schedule.remove();
        this.openParentForm();
      });
    }
    this.schedule.appendChild(scheduleFooter);
    this.body.appendChild(this.schedule);
    this.lastOpened = 'schedule';
  }

  async getClassTime(hours, minutes, date) {
    let nextMonday, nextWednesday;
    let now = date || new Date();
    let weekday = now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short'});
    switch (weekday) {
      case 'Sun': nextMonday = new Date(now.setDate(now.getDate() + 1)); break;
      case 'Mon':
        let nowHours = Number(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', hour12: false }));
        let nowMinutes = Number(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', minute: 'numeric' }));
        if (nowHours < hours || (nowHours === hours && nowMinutes < minutes)) nextMonday = now;
        else nextMonday = new Date(now.setDate(now.getDate() + 7));
        break;
      case 'Tue': nextMonday = new Date(now.setDate(now.getDate() + 6)); break;
      case 'Wed': nextMonday = new Date(now.setDate(now.getDate() + 5)); break;
      case 'Thu': nextMonday = new Date(now.setDate(now.getDate() + 4)); break;
      case 'Fri': nextMonday = new Date(now.setDate(now.getDate() + 3)); break;
      case 'Sat': nextMonday = new Date(now.setDate(now.getDate() + 2)); break;
    }
    let getHoliday = (date) => {
      let [weekday, month, day] = [date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short'}), Number(date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric'})), Number(date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', day: 'numeric'}))];
      if (month === 1 && day === 1) return 'New Year\'s Day';
      else if (weekday === 'Mon' && month === 5 && day >= 25 && day <= 31) return 'Memorial Day';
      else if (month === 7 && day === 4) return 'Independence Day';
      else if (weekday === 'Mon' && month === 9 && day >= 1 && day <= 7) return 'Labor Day';
      else if (weekday === 'Thu' && month === 11 && day >= 22 && day <= 28) return 'Thanksgiving';
      else if (month === 12 && day === 25) return 'Christmas';
    };
    for (let i = -1; i <= 5; i++) {
      let date = new Date(nextMonday);
      date.setDate(date.getDate() + i);
      let holiday = getHoliday(date);
      if (['Memorial Day', 'Labor Day'].includes(holiday)) {
        nextWednesday = new Date(nextMonday.setDate(nextMonday.getDate() + 2));
        break;
      } else if (holiday) {
        nextMonday = new Date(nextMonday.setDate(nextMonday.getDate() + 7));
        i = -1;
      }
    }
    let output;
    let utcOffset = `-${now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', timeZoneName: 'short' }).match(/\d+/)[0].padStart(2, '0')}:00`;
    if (nextWednesday) {
      nextWednesday = new Date(`${nextWednesday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric' })}-${nextWednesday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: '2-digit' })}-${nextWednesday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', day: '2-digit' })}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00${utcOffset}`);
      nextMonday = new Date(`${nextMonday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric' })}-${nextMonday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: '2-digit' })}-${nextMonday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', day: '2-digit' })}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00${utcOffset}`);
      output = { date: nextWednesday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', month: 'short', day: 'numeric'}), utc: getUTC(nextMonday) };
    } else {
      nextMonday = new Date(`${nextMonday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric' })}-${nextMonday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: '2-digit' })}-${nextMonday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', day: '2-digit' })}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00${utcOffset}`);
      output = { date: nextMonday.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', month: 'short', day: 'numeric' }), utc: getUTC(nextMonday) };
    }
    return output;
  }

  async getClassTopic() {
    let nextWeek = new Date(new Date().setDate(new Date().getDate() + 7));
    let scanDate = new Date(new Date().getFullYear(), 0, 1);
    let topicIndex = 14;
    while (scanDate < nextWeek) {
      scanDate = this.getClassTime(0, 0, scanDate).utc;
      topicIndex++;
      if (topicIndex === 15) topicIndex = 0;
    }
    return topicIndex;
  }

  async openSubscriptionsForm(plans, user) {
    this.subscriptionsForm = document.createElement('div');
    this.subscriptionsForm.classList.add('ui');
    this.subscriptionsForm.id = 'subscriptions-form';
    this.subscriptionsForm.appendChild(getHeader('Subscriptions', this.subscriptionsForm));

    let subscriptionsDiv = document.createElement('div');
    subscriptionsDiv.id = 'subscriptions-div';
    let subscriptionsTable = document.createElement('table');
    subscriptionsTable.id = 'subscriptions-table';
    let headerRow = subscriptionsTable.insertRow();
    let headerNameCell = document.createElement('th');
    headerNameCell.innerHTML = 'Name';
    headerRow.appendChild(headerNameCell);
    let headerGradeCell = document.createElement('th');
    headerGradeCell.innerHTML = 'Grade';
    headerRow.appendChild(headerGradeCell);
    let headerTimeCell = document.createElement('th');
    headerTimeCell.innerHTML = 'Time';
    headerRow.appendChild(headerTimeCell);
    let headerStopCell = document.createElement('th');
    headerRow.appendChild(headerStopCell);

    let subs = await get(`/parents/${user.uid}/subs`);
    if (!subs) {
      headerRow.remove();
      let row = subscriptionsTable.insertRow();
      let cell = row.insertCell();
      cell.innerHTML = 'No active subscriptions';
    } else {
      let stopSubscription = httpsCallable(window.firebaseFunctions, 'stopSubscription');
      let stopFunc = (name, sub, time) => async () => {
        this.subscriptionsForm.remove();
        let result = await stopSubscription({ subId: sub });
        if (result.data.success) {
          await remove(`/subs/${sub}`);
          await remove(`/parents/${user.uid}/subs/${sub}`);
          await getDialog(false, 'Success!', `The subscription for ${name} at ${time} has been stopped.`, 'Got it!');
          this.openSubscriptionsForm(plans, user);
        } else {
          await getDialog(false, 'Hold up!', `There was an error stopping the subscription.<br><br>If you just subscribed, please wait a few minutes and refresh before trying again.<br><br>For assistance, contact<br>learn@zebula.net`, 'Will do!');
          this.openSubscriptionsForm(plans, user);
        }
      }
      for (let sub in subs) {
        let row = subscriptionsTable.insertRow();
        let nameCell = row.insertCell();
        let name = subs[sub].split('-')[2]
        nameCell.innerHTML = name;
        let gradeCell = row.insertCell();
        gradeCell.innerHTML = subs[sub].split('-')[1].charAt(0);
        let timeCell = row.insertCell();
        let time = Object.entries(plans).find((plan) => plan[1].section === subs[sub].split('-')[1])[1].time;
        let formattedTime = `${time.split('–')[0]} ${time.split(' ')[1]} PT`;
        timeCell.innerHTML = formattedTime;
        let stopCell = row.insertCell();
        let stopButton = document.createElement('button');
        stopButton.classList.add('stop-button');
        stopButton.innerHTML = 'STOP';
        stopButton.addEventListener('click', async () => await getDialog(false, 'Hold up!', `Are you sure you want to<br>stop the subscription for<br>${name} at ${formattedTime}?`, 'Yes', stopFunc(name, sub, formattedTime), 'No'));
        stopCell.appendChild(stopButton);
      }
    }
    subscriptionsDiv.appendChild(subscriptionsTable);
    this.subscriptionsForm.appendChild(subscriptionsDiv);
    let scheduleFormButton = document.createElement('button');
    scheduleFormButton.id = 'schedule-form-button';
    scheduleFormButton.innerHTML = !this.lastOpened || this.lastOpened === 'home' ? 'Back to Home' : 'Back to Schedule';
    scheduleFormButton.addEventListener('click', () => {
      this.subscriptionsForm.remove();
      if (!this.lastOpened || this.lastOpened === 'home') this.openHome(user);
      else if (this.lastOpened === 'schedule') this.openSchedule(user);
    });
    this.subscriptionsForm.appendChild(scheduleFormButton);
    this.body.appendChild(this.subscriptionsForm);
  }

  async openTeacherForm() {
    this.teacherForm = document.createElement('div');
    this.teacherForm.classList.add('ui');
    this.teacherForm.id = 'teacher-form';
    this.teacherForm.appendChild(getHeader('Teacher', this.teacherForm));
  
    let emailInput = document.createElement('input');
    emailInput.classList.add('email-input');
    emailInput.placeholder = 'Email';
    emailInput.type = 'email';
    emailInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') passwordInput.focus(); });
    this.teacherForm.appendChild(emailInput);

    let passwordInput = document.createElement('input');
    passwordInput.classList.add('password-input');
    passwordInput.placeholder = 'Password';
    passwordInput.type = 'password';
    passwordInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.connectTeacher(emailInput.value, passwordInput.value); });
    this.teacherForm.appendChild(passwordInput);

    let logInButton = document.createElement('button');
    logInButton.classList.add('log-in-button');
    logInButton.innerHTML = 'Log In';
    logInButton.addEventListener('click', () => this.connectTeacher(emailInput.value, passwordInput.value));
    this.teacherForm.appendChild(logInButton);
  
    let registerButton = document.createElement('button');
    registerButton.classList.add('register-form-button');
    registerButton.innerHTML = 'Register';
    registerButton.addEventListener('click', () => {
      this.teacherForm.remove();
      this.openTeacherRegisterForm();
    });
  
    this.body.appendChild(this.teacherForm);
  }

  async connectTeacher(email, password) {
    useApp('teacher');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      // A mistyped password is an expected outcome, not a fault — same policy as the student
      // path (see connectStudent). Both call sites fire this without awaiting, so with no
      // catch here the rejection escaped to window.unhandledrejection and wrote a bare red
      // `Firebase: Error (auth/wrong-password)` into the teacher's log, with no context
      // saying it was just a login typo.
      const expected = ['auth/user-not-found', 'auth/wrong-password', 'auth/invalid-credential', 'auth/invalid-email', 'auth/too-many-requests'];
      if (!expected.some((code) => error?.code === code || error?.message?.includes(code))) logError(error, 'connecting a teacher');
      const passwordInput = this.teacherForm?.querySelector('.password-input');
      if (passwordInput) {
        passwordInput.value = '';
        passwordInput.placeholder = 'Try again';
      }
      return;
    }
    this.footer.style.opacity = 0;
    setTimeout(() => this.footer.remove(), 350);
    this.teacherForm.remove();
    this.openDash();
  }

  // A student account must never build the dashboard. Student logins are the ones Zebula
  // creates itself and are always @zebula.net (`${name}${password}${teacherUid}@zebula.net`,
  // openStudentForm) — a teacher registers with their own address, which is exactly the
  // distinction the already-signed-in gate in open() uses. When a student reaches a
  // teacher-only entry point, send them to the activity rather than a dashboard they can't
  // populate: z.teacher already holds the REAL teacher resolved from /codes/{code}/teacher,
  // and that teacher's roster record holds the name/color/emoji openApp needs. Returns true
  // if it took over the flow, false to let the caller apply its own handling.
  async redirectStudent() { try {
    if (!auth.currentUser?.email?.endsWith('@zebula.net')) return false;
    // No code means no activity to redirect TO — z.teacher is only set when one resolved.
    if (!z.teacher || z.teacher === auth.currentUser.uid) return false;
    // Roster-or-new: a student who has never completed a first login is still in /new/.
    let profile = await get(`/teachers/${z.teacher}/roster/${auth.currentUser.uid}`) || await get(`/teachers/${z.teacher}/new/${auth.currentUser.uid}`);
    // openApp's closing line rebuilds the login hash from all three of these, so an
    // incomplete profile can't drive the activity — fall back to the caller's block.
    if (!profile?.name || !profile?.color || !profile?.emoji) return false;
    write(z, ['student'], auth.currentUser.uid);
    write(z, ['uid'], auth.currentUser.uid);
    write(z, ['name'], profile.name);
    write(z, ['color'], profile.color);
    write(z, ['emoji'], profile.emoji);
    for (let key of Object.keys(z.color)) document.documentElement.style.setProperty(`--student-color-${key}`, z.color[key]);
    this.changeBackground();
    this.openApp();
    return true;
  } catch (error) { logError(error, 'redirecting a student account away from the dashboard'); return false; } }

  async openDash() {
    // Every entry path into the dashboard funnels through here — including connectTeacher,
    // which signs in with whatever the teacher form was given and calls straight through
    // without checking the account is a teacher. The z.teacher write below is what does the
    // damage: it replaces the code-derived teacher with the signed-in uid, so a student
    // account ends up reading /teachers/{studentUid}/roster — a node that doesn't exist,
    // which renders as an empty roster with no error and nothing in the console.
    if (await this.redirectStudent()) return;
    Dash = (await import('./dash')).Dash;
    write(z, ['style', 'dash'], document.createElement('style'));
    z.style.dash.classList.add('style', 'dash');
    z.style.dash.textContent = (await import('./dash.css')).default;
    document.head.appendChild(z.style.dash);
    write(z, ['teacher'], auth.currentUser.uid);
    onValue(`/teachers/${z.teacher}/models`, () => {});
    write(z, ['uid'], auth.currentUser.uid);
    // /play has no lesson behind it: the dashboard there is the game's (Dash's own record carries app 'game').
    new Dash(this.code === 'play' ? 'game' : this.app, this.code);
    this.close();
  }

  async openTeacherRegisterForm() {
    this.teacherRegisterForm = document.createElement('div');
    this.teacherRegisterForm.classList.add('ui');
    this.teacherRegisterForm.id = 'teacher-register-form';
    this.teacherRegisterForm.appendChild(getHeader('Teacher', this.teacherRegisterForm));
  
    let teacherNameInput = document.createElement('input');
    teacherNameInput.placeholder = 'Name';
    this.teacherRegisterForm.appendChild(teacherNameInput);
  
    let emailInput = document.createElement('input');
    emailInput.placeholder = 'Email';
    emailInput.type = 'email';
    this.teacherRegisterForm.appendChild(emailInput);
  
    let passwordInput = document.createElement('input');
    passwordInput.placeholder = 'Password';
    passwordInput.type = 'password';
    this.teacherRegisterForm.appendChild(passwordInput);
  
    let registerButton = document.createElement('button');
    registerButton.id = 'register-button';
    registerButton.innerHTML = 'Register';
    registerButton.addEventListener('click', () => {
      this.teacherRegisterForm.remove();
      useApp('teacher');
      createUserWithEmailAndPassword(auth, emailInput.value, passwordInput.value).then((userCredential) => {
        updateProfile(userCredential.user, { displayName: teacherNameInput.value });
        update(`/teachers/${userCredential.user.uid}`, { name: teacherNameInput.value, email: userCredential.user.email });
        this.openDash();
      }).catch((error) => {
        this.body.appendChild(this.teacherRegisterForm);
        logError(error, 'registering a teacher account');
        getDialog(false, 'Oops...', error.message || 'Registration failed. Please try again.', 'Got it!');
      });
    });
    this.teacherRegisterForm.appendChild(registerButton);
    this.body.appendChild(this.teacherRegisterForm);
  }

  async openFooter() {
    this.footer = document.createElement('div');
    this.footer.id = 'footer';

    let footerHomeDiv = document.createElement('div');
    footerHomeDiv.id = 'footer-home-div';
    this.footerHomeButton = document.createElement('button');
    this.footerHomeButton.id = 'footer-home-button';
    this.footerHomeButton.innerHTML = z.svg.icons.zebulatext;
    footerHomeDiv.appendChild(this.footerHomeButton);
    this.footer.appendChild(footerHomeDiv);

    let contactButton = document.createElement('button');
    contactButton.classList.add('footer-button');
    contactButton.innerHTML = 'contact';
    contactButton.style.cursor = 'pointer';
    contactButton.addEventListener('click', () => getDialog(false, 'Hi there!', 'For any questions, concerns, or feedback, contact us at:<br>learn@zebula.net', 'Got it!'));

    let privacyButton = document.createElement('button');
    privacyButton.classList.add('footer-button');
    privacyButton.innerHTML = 'privacy';
    privacyButton.style.cursor = 'pointer';
    let privacyDiv = document.createElement('div');
    privacyDiv.classList.add('ui');
    privacyDiv.id = 'privacy-policy';
    privacyDiv.appendChild(getHeader('Privacy Policy', privacyDiv));
    let privacyText = document.createElement('div');
    privacyText.innerHTML = "Last updated: July 20th, 2023<br><br>Welcome to Zebula, an educational platform dedicated to enriching the learning journey of students. We take your privacy very seriously, and this policy is designed to share what information we collect and how we use it.<br><br>1. What information we collect<br>We collect personal information from our users in a variety of ways, including, but not limited to:<br>• User-provided information: The only personal information provided by the user is their first name, which is used to access the service along with a selected color and picture as a password.<br>• Automatically collected information: We may collect certain information automatically, such as IP address, device ID, type of device/operating system used to access our services, and the date and time of use.<br><br>2. How we use information<br>We use the information collected primarily for the following purposes:<br>• To provide, maintain, and improve our services<br>• To personalize and enhance the user experience<br><br>3. Sharing of information<br>We do not rent or sell your personal information to third parties. We may disclose your information only under the following circumstances:<br>• When required by law<br>• When we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request.<br><br>4. Children’s privacy<br>We comply with the Children's Online Privacy Protection Act (COPPA). We do not knowingly collect personal information from children under 13 without verifiable parental consent. Student accounts can only be created by a teacher account, and only individuals and institutions that have expressly agreed to follow COPPA policy may access teacher accounts. If we learn that we have collected any personal information from a child under the age of 13 without verifiable parental consent, we will delete that information from our database as quickly as possible.<br><br>5. Your choices about your information<br>You can review, correct, update, or delete your personal information by contacting us directly at learn@zebula.net.<br><br>6. Changes to this privacy policy<br>We may update our privacy policy from time to time. Thus, you are advised to review this page periodically for any changes. We will notify you of any changes by posting the new privacy policy on this page.";
    privacyDiv.appendChild(privacyText);
    privacyButton.addEventListener('click', () => getDialog(false, 'Privacy Policy', privacyText.innerHTML, 'Got it!'));

    if (!this.enrolling) {
      this.footerRoleButton = document.createElement('button');
      this.footerRoleButton.classList.add('footer-button');
      this.footerRoleButton.innerHTML = 'teacher';
      this.footerRoleButton.style.cursor = 'pointer';
      this.event.push({ element: this.footerRoleButton, type: 'click', function: () => {
        if (document.body.contains(this.home) || document.body.contains(this.parentForm) || document.body.contains(this.parentRegisterForm) || document.body.contains(this.schedule)) {
          z.loading.style.display = '';
          z.loading.style.opacity = 1;
          if (this.home) this.home.remove();
          if (this.parentForm) this.parentForm.remove();
          if (this.parentRegisterForm) this.parentRegisterForm.remove();
          if (this.schedule) this.schedule.remove();
          this.openTeacherForm();
          this.footerRoleButton.innerHTML = 'parent';
        } else if (document.body.contains(this.studentForm)) {
          this.studentForm.remove();
          z.background.style.transition = 'opacity 350ms ease-in-out';
          z.background.style.opacity = 0;
          setTimeout(() => {
            z.background.style.transition = 'none';
            z.background.style.background = '';
            z.background.style.backgroundColor = '';
            z.background.style.opacity = '';
            z.background.offsetHeight;
            z.background.style.transition = '';
          }, 350);
          this.openTeacherForm();
          this.footerRoleButton.innerHTML = 'student';
        } else if (document.body.contains(this.teacherForm) || document.body.contains(this.teacherRegisterForm)) {
          this.teacherForm.remove();
          if (this.teacherRegisterForm) this.teacherRegisterForm.remove();
          if (code) this.openStudentForm();
          else this.openParentForm();
          this.footerRoleButton.innerHTML = 'teacher';
        }
      }});
    }

    let footerLinks = document.createElement('div');
    footerLinks.appendChild(contactButton);
    footerLinks.appendChild(privacyButton);
    if (!this.enrolling) footerLinks.appendChild(this.footerRoleButton);
    this.footer.appendChild(footerLinks);
    this.page.appendChild(this.footer);
  }

  rain() {
    z.background.classList.add('rain');
    let raindrop = document.createElement('div');
    raindrop.classList.add('raindrop');
    let symbols = ['$', '$', '$', '+', '−', '×', '÷'];
    raindrop.innerText = symbols[Math.floor(Math.random() * symbols.length)];
    raindrop.style.left = `${Math.random() * window.innerWidth}px`;
    let duration = Math.random() * 20 + 10;
    raindrop.style.animationDuration = `${duration}s`;
    raindrop.style.top = '-50px';
    z.background.appendChild(raindrop);
    setTimeout(() => { raindrop.style.opacity = 1; }, 100);
    setTimeout(() => { raindrop.style.opacity = 0; }, (duration * 1000 - 500));
    setTimeout(() => { raindrop.remove(); }, (duration * 1000));
    if (document.querySelectorAll('.raindrop').length === 1) setInterval(() => this.rain(), 1000);
  }

  twinkle() {
    if (document.getElementById('dash')) return; // Skip when dashboard is active
    let starLimit = window.innerHeight * window.innerWidth / 50000;
    if (document.querySelectorAll('.star').length > starLimit) return;
    z.background.classList.add('twinkle');
    let pageRect = this.page ? this.page.getBoundingClientRect() : null;
    let star = document.createElement('div');
    star.classList.add('star', 'twinkling');
    star.innerHTML = z.svg.icons.zebulastar;
    star.style.height = `${Math.random() * 18 + 6}px`;
    star.style.opacity = 0;
    star.style.width = star.style.height;
    let [x, y] = [Math.random() * window.innerWidth, Math.random() * window.innerHeight];
    for (let star of document.querySelectorAll('.star')) if ((Math.abs(x - Number(star.style.left.replace('px', ''))) < 72 && Math.abs(y - Number(star.style.top.replace('px', ''))) < 72) || (pageRect && x > pageRect.left - 36 && x < pageRect.right + 36 &&  y > pageRect.top - 36 && y < pageRect.bottom + 36) || y < 38) return this.twinkle();
    [star.style.left, star.style.top] = [`${x}px`, `${y}px`];
    let duration = (Math.random() * 20 + 10) * 1000;
    z.background.appendChild(star);
    if (document.querySelectorAll('.star').length < starLimit) this.twinkle();
    setTimeout(() => {
      let brightness = Math.random() * 0.2 + 0.5;
      star.style.opacity = brightness;
      for (let i = 1; i < 5; i++) {
        setTimeout(() => { star.style.opacity = brightness - 0.25 }, (duration * (i / 5)));
        setTimeout(() => { star.style.opacity = brightness; }, (duration * (1 / 5) + 500));
      }
    }, 100);
    setTimeout(() => { star.style.opacity = 0; }, (duration - 500));
    setTimeout(() => { star.remove(); this.twinkle(); }, duration);
  }

  close() {
    this.event.forEach((event) => event.element.removeEventListener(event.type, event.function));
  }
}